// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Logomark } from '../ui/Logomark.js';
import * as bip39 from 'bip39';
import { registerUser, postKeyBundle } from '../../lib/syncClient.js';
import {
  deriveAuthTokenViaWorker,
  deriveRecoveryProofViaWorker,
  generateDekViaWorker,
  buildKeyMaterialBundle,
} from '../../store/useCryptoWorker.js';
import { useApp } from '../../store/AppContext.js';
import { evaluatePassword } from '../../lib/passwordStrength.js';
import { ServerUrlField } from './ServerUrlField.js';

function mnemonicToEntropyBytes(mnemonic: string): Uint8Array {
  const hex = bip39.mnemonicToEntropy(mnemonic);
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

interface RegisterFormProps {
  syncUrl: string;
  worker: Worker;
  onSwitchToLogin: () => void;
}

type Step = 'form' | 'mnemonic' | 'confirm';

export function RegisterForm({ syncUrl, worker, onSwitchToLogin }: RegisterFormProps) {
  const { t } = useTranslation(['auth', 'common']);
  const { dispatch } = useApp();

  const [step, setStep] = useState<Step>('form');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mnemonic, setMnemonic] = useState('');
  const [confirmWords, setConfirmWords] = useState<Record<number, string>>({});
  const [challengeIndices, setChallengeIndices] = useState<number[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');

  const [pendingToken, setPendingToken] = useState('');
  const [pendingRefreshToken, setPendingRefreshToken] = useState('');
  const [pendingKeyData, setPendingKeyData] = useState<ArrayBuffer | null>(null);

  const inputCls = 'w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none placeholder-text-subtle';
  const labelCls = 'block text-sm text-text-muted mb-1';

  async function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!username.trim()) { setError(t('register.usernameRequired')); return; }
    if (username.length < 3) { setError(t('register.usernameMinLength')); return; }
    const strength = evaluatePassword(password);
    if (!strength.ok) { setError(t(`passwordStrength.${strength.messageKey}`, { ...strength.messageParams })); return; }
    if (password !== confirmPassword) { setError(t('register.passwordsMismatch')); return; }

    setLoading(true);
    try {
      setLoadingStep(t('register.generatingPhrase'));
      const newMnemonic = bip39.generateMnemonic(256);
      const entropy = mnemonicToEntropyBytes(newMnemonic);

      setLoadingStep(t('login.deriving'));
      const serverAuthToken = await deriveAuthTokenViaWorker(worker, password, username.trim());

      setLoadingStep(t('register.generatingKey'));
      // The DEK is what actually encrypts the ledger; wrap it under both the
      // password (Argon2id) and the mnemonic (HKDF) so either can recover it.
      const keyData = await generateDekViaWorker(worker);

      setLoadingStep(t('register.encryptingKeys'));
      const bundle = await buildKeyMaterialBundle(worker, keyData, password, entropy, newMnemonic);

      setLoadingStep(t('register.derivingRecoveryProof'));
      const recoveryProof = await deriveRecoveryProofViaWorker(worker, entropy, username.trim());

      setLoadingStep(t('register.creatingAccount'));
      const result = await registerUser(syncUrl, username.trim(), serverAuthToken);

      setLoadingStep(t('register.uploadingKeys'));
      await postKeyBundle({ baseUrl: syncUrl, token: result.token }, bundle, recoveryProof);

      const words = newMnemonic.split(' ');
      const indices: number[] = [];
      while (indices.length < 3) {
        const idx = Math.floor(Math.random() * words.length);
        if (!indices.includes(idx)) indices.push(idx);
      }
      indices.sort((a, b) => a - b);

      setPendingToken(result.token);
      setPendingRefreshToken(result.refreshToken);
      setPendingKeyData(keyData);
      setMnemonic(newMnemonic);
      setChallengeIndices(indices);
      setStep('mnemonic');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('register.registrationFailed'));
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  }

  function handleMnemonicContinue() {
    setConfirmWords({});
    setError('');
    setStep('confirm');
  }

  function handleConfirmSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    const words = mnemonic.split(' ');
    for (const idx of challengeIndices) {
      const expected = words[idx];
      const entered = (confirmWords[idx] ?? '').trim().toLowerCase();
      if (entered !== expected) {
        setError(t('register.wordIncorrect', { n: idx + 1 }));
        return;
      }
    }

    if (!pendingKeyData) {
      setError(t('register.keyDataMissing'));
      return;
    }

    dispatch({
      type: 'AUTH_SUCCESS',
      username: username.trim(),
      token: pendingToken,
      refreshToken: pendingRefreshToken,
      keyData: pendingKeyData,
      syncUrl,
    });
  }

  const mnemonicWords = mnemonic ? mnemonic.split(' ') : [];

  if (step === 'mnemonic') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-4">
        <div className="w-full max-w-lg bg-bg-surface border border-border p-6">
          <h2 className="text-lg font-semibold text-text mb-2">{t('register.yourRecoveryPhrase')}</h2>
          <p className="text-sm text-text-muted mb-5">
            {/* Children must be exactly [text, <strong>, text] so the element is
                node 1, matching the <1></1> markers in every locale's string —
                an extra {' '} child shifts the index and the bold text vanishes. */}
            <Trans t={t} i18nKey="register.recoveryPhraseWarning">
              Write these 24 words down and store them securely. They are the <strong className="text-text">only way</strong> to recover your account if you forget your password. Never share them with anyone.
            </Trans>
          </p>

          <div className="grid grid-cols-3 gap-1.5 mb-5 p-4 bg-bg border border-border-strong">
            {mnemonicWords.map((word, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="text-text-subtle text-xs font-mono w-6 text-right shrink-0">{i + 1}.</span>
                <span className="font-mono text-sm text-text">{word}</span>
              </div>
            ))}
          </div>

          <div className="border border-negative p-3 mb-5">
            <p className="text-negative text-sm">
              {t('register.cannotRecover')}
            </p>
          </div>

          <button
            onClick={handleMnemonicContinue}
            className="w-full bg-accent text-white py-2.5 text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            {t('register.wroteItDown')}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-bg-surface border border-border p-6">
          <h2 className="text-lg font-semibold text-text mb-2">{t('register.confirmPhrase')}</h2>
          <p className="text-sm text-text-muted mb-5">
            {t('register.confirmPrompt')}
          </p>

          {error && (
            <div className="border border-negative text-negative px-3 py-2 mb-4 text-sm">{error}</div>
          )}

          <form onSubmit={handleConfirmSubmit} className="space-y-4">
            {challengeIndices.map((idx) => (
              <div key={idx}>
                <label className={labelCls}>{t('register.wordLabel', { n: idx + 1 })}</label>
                <input
                  type="text"
                  value={confirmWords[idx] ?? ''}
                  onChange={(e) => setConfirmWords((prev) => ({ ...prev, [idx]: e.target.value }))}
                  className={`${inputCls} font-mono`}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>
            ))}

            <button
              type="submit"
              className="w-full bg-accent text-white py-2.5 text-sm font-medium hover:bg-accent-hover transition-colors"
            >
              {t('register.enterApp')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-2xl font-semibold text-text mb-1">
            <Logomark size={24} />
            {t('common:appName')}
          </div>
          <p className="text-sm text-text-muted">{t('common:tagline')}</p>
        </div>

        <div className="bg-bg-surface border border-border p-6">
          <h2 className="text-md font-semibold text-text mb-5">{t('register.createAccount')}</h2>

          {error && (
            <div className="border border-negative text-negative px-3 py-2 mb-4 text-sm">{error}</div>
          )}

          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div>
              <label className={labelCls}>{t('login.username')}</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={inputCls}
                autoComplete="username"
                autoFocus
              />
            </div>

            <div>
              <label className={labelCls}>{t('login.password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                autoComplete="new-password"
              />
              <p className="text-xs text-text-subtle mt-1">
                {t('register.recoveryGeneratedNote')}
              </p>
            </div>

            <div>
              <label className={labelCls}>{t('register.confirmPassword')}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputCls}
                autoComplete="new-password"
              />
            </div>

            <ServerUrlField />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-accent text-white py-2.5 text-sm font-medium hover:bg-accent-hover disabled:opacity-40 transition-colors"
            >
              {loading ? (loadingStep || t('register.creatingAccount')) : t('common:actions.continue')}
            </button>

            <p className="text-xs text-text-subtle">
              {t('register.agreePrefix')}
              <a
                href="https://hushold.app/terms.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-muted underline hover:text-text"
              >
                {t('register.agreeTermsLink')}
              </a>
              {t('register.agreeAnd')}
              <a
                href="https://hushold.app/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-muted underline hover:text-text"
              >
                {t('register.agreePrivacyLink')}
              </a>
              {t('register.agreeSuffix')}
            </p>
          </form>
        </div>

        <div className="mt-4 text-sm text-text-muted px-1">
          <button onClick={onSwitchToLogin} className="hover:text-text transition-colors">
            {t('register.haveAccount')}
          </button>
        </div>
      </div>
    </div>
  );
}
